import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { geoMercator, GeoPath, geoPath } from 'd3-geo'

import { PlaceLabel } from 'components/PlaceLabel'
import { RegionLabel } from 'components/RegionLabel'
import { useElementSize } from 'usehooks-ts'
import * as d3 from 'd3'

const BaseMap = require('../GeoJSON/country.json')
const RegionsMap = require('../GeoJSON/regions.json')
const AreaOfInterest = require('../GeoJSON/AreaOfInterest.json')

export const UkraineMap = ({
  data,
  height,
  russianControl,
  russianAdvance,
  regionLabels,
  mapLabels,
  colorMode,
  index,
  zoomToRegionLabels,
}: {
  data: any
  height: number
  russianControl?: any
  russianAdvance?: any
  regionLabels?: RegionLabelType[]
  mapLabels?: MapLabelType[]
  colorMode: 'light' | 'dark'
  index: number
  zoomToRegionLabels?: boolean
}) => {
  const [container, size] = useElementSize()
  const [isMobile, setMobile] = useState(false)
  const [{ width }, setSize] = useState({
    width: 100,
  })
  const svgRef = React.useRef<SVGSVGElement | null>(null)
  const regionLabelsRef = React.useRef<SVGGElement | null>(null)
  let projection = geoMercator().fitSize([width, height], AreaOfInterest as any)

  useEffect(() => {
    const selectedMunicipalityElement = d3
      .select(regionLabelsRef.current)
      .node() as SVGGraphicsElement
    const distanceToObject = isMobile ? 100 : 550

    const bbox = selectedMunicipalityElement.getBBox()
    if (isMobile) {
      if (
        svgRef?.current &&
        regionLabelsRef?.current &&
        mapLabels &&
        zoomToRegionLabels &&
        isMobile
      ) {
        d3.select(svgRef.current)
          .transition()
          .ease(d3.easeCubicOut)
          .duration(1000)
          .attr(
            'viewBox',
            bbox.x -
              distanceToObject +
              ' ' +
              (bbox.y - distanceToObject) +
              ' ' +
              (bbox.width + distanceToObject * 2) +
              ' ' +
              (bbox.height + distanceToObject * 2)
          )
      } else {
        d3.select(svgRef.current)
        .transition()
        .ease(d3.easeCubicOut)
        .duration(1000)
        .attr(
          'viewBox',
          '0 0 ' + width + ' ' + height
          )
      }
    }
  }, [mapLabels])
  useEffect(() => {
    if (size.width === width) return

    setSize({ width: size.width })
    setMobile(size.width < 712)
    projection = geoMercator().fitSize(
      [size.width, size.height],
      AreaOfInterest as any
    )
  }, [size])

  const geoPathGenerator: GeoPath<any, any> = geoPath().projection(projection)

  return (
    <MapContainer ref={container} className='MapContainer'>
      <StyledSVG
        ref={svgRef}
        width={width}
        height={height}
        viewBox={'0 0 ' + width + ' ' + height}
      >
        <defs>
          <pattern
            id='diagonal-stripe-1'
            patternUnits='userSpaceOnUse'
            width='4'
            height='4'
          >
            <path
              d='M-1,1 l2,-2
           M0,4 l4,-4
           M3,5 l2,-2'
              style={{
                stroke: colorMode === 'light' ? '#6f7187' : '#EFECE0',
                strokeWidth: 1.5,
              }}
            />
          </pattern>
        </defs>
        <g>
          <g className='regions'>
            <Regions d={geoPathGenerator(RegionsMap)} colorMode={colorMode} />
          </g>
          <g className='regions'>
            <Regions d={geoPathGenerator(RegionsMap)} colorMode={colorMode} />
          </g>
          <g className='occupied'>
            {russianControl && (
              <Occupied
                d={geoPathGenerator(russianControl)}
                colorMode={colorMode}
              />
            )}
          </g>
          <g className='advance'>
            {russianAdvance && (
              <Advances
                d={geoPathGenerator(russianAdvance)}
                colorMode={colorMode}
              />
            )}
          </g>
          <g className='country'>
            <CountrySecondOutline d={geoPathGenerator(BaseMap)} />
            <CountryOutline d={geoPathGenerator(BaseMap)} />
          </g>
          <g className='bombs'>
            {data.features
              .sort((a, b) => {
                if (a.properties?.event?.toLowerCase()?.includes('air'))
                  return 1
                if (b.properties?.event?.toLowerCase()?.includes('air'))
                  return -1
                return 0
              })
              .map(({ geometry, properties }, i: number) => {
                let pos = projection(geometry.coordinates)
                let isAir = properties?.event?.toLowerCase()?.includes('air')
                return (
                  <React.Fragment key={'Bomb_' + i}>
                    {index !== 16 && (
                      <Bomb
                        pos={pos}
                        r={isMobile ? 3 : 4}
                        isAir={false}
                        colorMode={colorMode}
                      />
                    )}
                    {index === 16 && (
                      <Building colorMode={colorMode} pos={pos} />
                    )}
                  </React.Fragment>
                )
              })}
          </g>

          <g ref={regionLabelsRef} className='region_labels'>
            {regionLabels &&
              regionLabels.map((m, i) => {
                let pos = projection(m.pos)
                return (
                  <RegionLabel
                    key={'Region_' + i + m.pos}
                    x={pos[0]}
                    y={pos[1]}
                    data={{
                      pos: m.pos,
                      r: [(width / 100) * m.r[0], (width / 100) * m.r[1]],
                    }}
                  />
                )
              })}
          </g>
          <g className='map_labels'>
            {mapLabels &&
              mapLabels.map((m, i) => {
                let pos = projection(m.pos)
                return (
                  <PlaceLabel
                    key={'Place_' + i}
                    x={pos[0]}
                    y={pos[1]}
                    hideLine={m.hideLine}
                    data={m}
                  />
                )
              })}
          </g>
        </g>
      </StyledSVG>
    </MapContainer>
  )
}

const Building = ({
  colorMode,
  pos,
}: {
  colorMode: 'light' | 'dark'
  pos: [number, number]
}) => {
  return (
    <path
      transform={`translate(${pos[0]},${pos[1]})`}
      d={
        'M4.9137,3.4993l1.793-1.793c.3901-.3896,.3901-1.0244,0-1.4141-.3896-.3896-1.0244-.3896-1.4141,0l-1.793,1.793L1.7067,.2922C1.317-.0974,.6823-.0974,.2926,.2922-.0975,.6819-.0975,1.3167,.2926,1.7063l1.793,1.793L.2926,5.2922c-.3901,.3896-.3901,1.0244,0,1.4141,.3779,.3779,1.0361,.3779,1.4141,0l1.793-1.793,1.793,1.793c.189,.189,.4399,.293,.707,.293s.5181-.104,.707-.293c.3901-.3896,.3901-1.0244,0-1.4141l-1.793-1.793Z'
      }
      opacity={0.9}
      fill={colorMode === 'light' ? '#D3343F' : '#D3343F'}
    />
  )
}
const Bomb = ({
  pos,
  r,
  isAir,
  colorMode,
}: {
  pos: [number, number]
  r: number
  isAir: boolean
  colorMode: 'light' | 'dark'
}) => {
  return (
    <BombMarker
      cx={pos[0]}
      cy={pos[1]}
      r={r}
      opacity={0.9}
      current={isAir}
      colorMode={colorMode}
      fill={
        isAir
          ? colorMode === 'light'
            ? '#202346'
            : '#378EBD'
          : colorMode === 'light'
          ? '#E78D38'
          : '#E78D38'
      }
    />
  )
}
const MapContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 90%;
  display: inline-block;
  flex: 0 0 53%;
  margin-bottom: 30px;
  /* overflow: hidden; */
  @media only screen and (max-width: 711px) {
    flex: 0 0 45%;
  }
`
const StyledSVG = styled.svg`
  overflow: visible;
  mask-image: linear-gradient(to left, black 92%, transparent 100%);
  /* border: 1px solid #e5e5e5; */
`

const CountryOutline = styled.path`
  fill: none;
  stroke-linejoin: round;
  stroke: #7d7d7d;
  stroke: var(--text-color);
  opacity: 0.7;
`
const CountrySecondOutline = styled.path`
  fill: none;
  stroke-linejoin: round;
  opacity: 0.7;
  stroke: var(--site-background);
  stroke-width: 5;
  opacity: 0.7;
`
const Regions = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: var(--site-background);
  opacity: 1;
  stroke: ${({ colorMode }) => (colorMode === 'light' ? '#d4d4d4' : '#3e3e3e')};
  stroke-width: 1;
`

const Occupied = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: ${({ colorMode }) => (colorMode === 'light' ? '#6f7187' : '#EFECE0')};
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.3 : 0.15)};
  stroke: var(--site-background);
  stroke-width: 2;
`
const Advances = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: ${({ colorMode }) => (colorMode === 'light' ? '#6f7187' : '#EFECE0')};
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.4 : 0.2)};
  fill: url(#diagonal-stripe-1);
  stroke: none;
  stroke-width: 1;
  animation: fade-in-low 0.5s cubic-bezier(0.39, 0.575, 0.565, 1) both;
  animation-delay: 0.1s;
  @keyframes fade-in-low {
    0% {
      opacity: 0;
    }
    100% {
      opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.4 : 0.2)};
    }
  }
`
const BombMarker = styled.circle<{
  current: boolean
  colorMode: 'light' | 'dark'
}>`
  fill: ${({ current, colorMode }) =>
    current
      ? colorMode === 'light'
        ? '#202346'
        : '#378EBD'
      : colorMode === 'light'
      ? '#E78D38'
      : '#E78D38'};
`
